<script setup lang="ts" generic="T extends TableData">
import { PhCornersOut } from "@phosphor-icons/vue"
import type { TableColumn, TableData, TableRow } from "@nuxt/ui"
import { computed, ref, useSlots } from "vue"
import DashboardEmptyState from "./DashboardEmptyState.vue"
import TableSortButton from "./TableSortButton.vue"

type TableSorting = Array<{ id: string; desc: boolean }>
type TableMetaLike = Record<string, unknown>
type TableUiLike = Record<string, unknown>

const props = withDefaults(
  defineProps<{
    columns: TableColumn<T>[]
    data: T[]
    meta?: TableMetaLike
    cardClass?: string
    emptyDescription?: string
    emptyTitle?: string
    eyebrow?: string
    maximizeLabel?: string
    modalDescription?: string
    modalScrollClass?: string
    modalScrollTestId?: string
    modalTitle?: string
    scrollClass?: string
    scrollTestId?: string
    sorting: TableSorting
    sticky?: boolean | "header" | "footer"
    title?: string
    ui?: TableUiLike
  }>(),
  {
    cardClass: "",
    emptyDescription: "",
    emptyTitle: "",
    eyebrow: "",
    maximizeLabel: "",
    modalDescription: "",
    modalScrollClass: "",
    modalScrollTestId: undefined,
    modalTitle: "",
    scrollClass: "",
    scrollTestId: undefined,
    meta: undefined,
    sticky: false,
    title: "",
    ui: undefined
  }
)

const emit = defineEmits<{
  select: [event: Event, row: TableRow<T>]
  "update:sorting": [value: TableSorting]
}>()

const slots = useSlots()
const expanded = ref(false)
const tableSorting = computed({
  get: () => props.sorting,
  set: (value: TableSorting) => emit("update:sorting", value)
})
const reservedSlotNames = new Set(["actions", "empty", "header", "title"])
const forwardedSlotNames = computed(() =>
  Object.keys(slots).filter((name) => !name.endsWith("-header") && !reservedSlotNames.has(name))
)
const sortableHeaders = computed(() =>
  props.columns.flatMap((column) => {
    const id = columnId(column)
    const label = columnLabel(column)
    return id && label ? [{ label, slot: `${id}-header` }] : []
  })
)
const hasRows = computed(() => props.data.length > 0)
const resolvedTitle = computed(() => props.title || "Table")
const resolvedEmptyTitle = computed(() => props.emptyTitle || `No ${resolvedTitle.value.toLowerCase()} rows`)
const resolvedEmptyDescription = computed(() => props.emptyDescription || "No rows match the current data and filters.")
const resolvedMaximizeLabel = computed(() => props.maximizeLabel || `Expand ${resolvedTitle.value}`)
const resolvedModalTitle = computed(() => props.modalTitle || resolvedTitle.value)
const resolvedModalDescription = computed(
  () => props.modalDescription || props.emptyDescription || "Review the full table in a larger workspace."
)

function columnId(column: TableColumn<T>): string {
  const record = column as unknown as Record<string, unknown>
  const id = record.id
  const accessorKey = record.accessorKey

  if (typeof id === "string" && id.length > 0) {
    return id
  }
  if (typeof accessorKey === "string" && accessorKey.length > 0) {
    return accessorKey
  }

  return ""
}

function columnLabel(column: TableColumn<T>): string {
  const header = (column as unknown as Record<string, unknown>).header
  return typeof header === "string" ? header : ""
}

function handleSelect(eventOrRow: Event | TableRow<T>, maybeRow?: TableRow<T>) {
  const row = maybeRow ?? (eventOrRow instanceof Event ? undefined : eventOrRow)
  if (!row) {
    return
  }

  emit("select", eventOrRow instanceof Event ? eventOrRow : new Event("select"), row)
}
</script>

<template>
  <template v-if="title">
    <UCard
      as="section"
      :class="[
        'dashboard-card dashboard-card--static dashboard-table-card dashboard-table-card--with-header',
        cardClass
      ]"
      :ui="{
        root: 'dashboard-table-card-root',
        header: 'dashboard-table-card-header',
        body: 'dashboard-table-card-body'
      }"
    >
      <template #header>
        <div class="min-w-0">
          <p v-if="eyebrow" class="dashboard-label">{{ eyebrow }}</p>
          <h2>{{ title }}</h2>
        </div>
        <div class="dashboard-table-card-actions">
          <slot name="actions" />
          <UTooltip
            :text="resolvedMaximizeLabel"
            :ui="{ content: 'dashboard-tooltip-content', text: 'dashboard-tooltip-text' }"
          >
            <UButton
              color="neutral"
              variant="ghost"
              size="xs"
              :icon="PhCornersOut"
              :aria-label="resolvedMaximizeLabel"
              class="dashboard-icon-button"
              @click="expanded = true"
            />
          </UTooltip>
        </div>
      </template>

      <UScrollArea
        v-if="hasRows"
        :data-testid="scrollTestId"
        :class="['dashboard-table-scroll dashboard-table-scroll--fill dashboard-table-scroll--flush', scrollClass]"
      >
        <UTable
          v-model:sorting="tableSorting"
          :columns="columns"
          :data="data"
          :meta="meta"
          :sticky="sticky"
          :ui="ui"
          @select="handleSelect"
        >
          <template v-for="slotName in forwardedSlotNames" #[slotName]="slotProps">
            <slot :name="slotName" v-bind="slotProps" />
          </template>
          <template v-for="header in sortableHeaders" #[header.slot]="{ column }">
            <TableSortButton :label="header.label" :column="column" />
          </template>
        </UTable>
      </UScrollArea>
      <div v-else class="dashboard-table-empty">
        <slot name="empty">
          <DashboardEmptyState :title="resolvedEmptyTitle" :description="resolvedEmptyDescription" centered />
        </slot>
      </div>

      <UModal
        v-model:open="expanded"
        fullscreen
        :title="resolvedModalTitle"
        :description="resolvedModalDescription"
        :close="{ color: 'neutral', variant: 'ghost', class: 'dashboard-icon-button dashboard-modal-close-button' }"
        :ui="{
          content: 'dashboard-modal-content dashboard-modal-content--fullscreen-table',
          header: 'dashboard-modal-header',
          close: 'dashboard-modal-close-button',
          body: 'dashboard-modal-body dashboard-modal-body--fullscreen-table',
          footer: 'dashboard-modal-footer'
        }"
      >
        <template #body>
          <div
            :data-testid="modalScrollTestId"
            :class="[
              'dashboard-table-scroll dashboard-table-scroll--modal dashboard-table-scroll--fill',
              modalScrollClass || scrollClass
            ]"
          >
            <UTable
              v-if="hasRows"
              v-model:sorting="tableSorting"
              :columns="columns"
              :data="data"
              :meta="meta"
              :sticky="sticky"
              :ui="ui"
              @select="handleSelect"
            >
              <template v-for="slotName in forwardedSlotNames" #[slotName]="slotProps">
                <slot :name="slotName" v-bind="slotProps" />
              </template>
              <template v-for="header in sortableHeaders" #[header.slot]="{ column }">
                <TableSortButton :label="header.label" :column="column" />
              </template>
            </UTable>
            <div v-else class="dashboard-table-empty dashboard-table-empty--modal">
              <slot name="empty">
                <DashboardEmptyState :title="resolvedEmptyTitle" :description="resolvedEmptyDescription" centered />
              </slot>
            </div>
          </div>
        </template>
      </UModal>
    </UCard>
  </template>

  <UTable
    v-else
    v-model:sorting="tableSorting"
    :columns="columns"
    :data="data"
    :meta="meta"
    :sticky="sticky"
    :ui="ui"
    @select="handleSelect"
  >
    <template v-for="slotName in forwardedSlotNames" #[slotName]="slotProps">
      <slot :name="slotName" v-bind="slotProps" />
    </template>
    <template v-for="header in sortableHeaders" #[header.slot]="{ column }">
      <TableSortButton :label="header.label" :column="column" />
    </template>
  </UTable>
</template>
